import React, { forwardRef, useEffect, useId, useRef, useState } from "react";
import "./Toggle.scss";

// Fontaine M14 — Toggle (switch). Visual contract: Style Guide V2 `toggle-*` specimens
// (44×26 track, 20px knob; on = accent, tones active = green, reserved = amber) and the listing card's
// ON / OFF · RSVD pills. Auto-saving: the change persists the moment it is made — no Save button.

/**
 * @param {boolean} checked
 * @param {(next: boolean) => void|Promise<void>} [onChange]   return a promise to get optimistic update + rollback
 * @param {ReactNode} [label]                                  visible label (clickable); pass `aria-label` when hidden
 * @param {"sm"|"md"} [size="md"]                              sm = 36×20, md = 44×26
 * @param {"default"|"active"|"reserved"|"pending"} [tone="default"]   on-state colour: accent · success · warning (reserved and pending share warning)
 * @param {"start"|"end"} [labelPosition="end"]
 * @param {string} [statusOn] / [statusOff]                    e.g. "ON"/"OFF", "RSVD" — small caps text beside the switch
 * @param {boolean} [bare]                                     legacy no-op (pill chrome is gone)
 */
const Toggle = forwardRef(function Toggle(
  {
    checked = false,
    onChange,
    label,
    size = "md",
    tone = "default",
    labelPosition = "end",
    statusOn,
    statusOff,
    disabled = false,
    className = "",
    id,
    bare, // eslint-disable-line no-unused-vars -- legacy prop accepted and ignored
    ...rest
  },
  ref
) {
  const autoId = useId();
  const baseId = id || `ft-toggle-${autoId}`;
  const [pending, setPending] = useState(false);
  const [optimistic, setOptimistic] = useState(null); // null = follow `checked`
  const [error, setError] = useState(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  useEffect(() => { setOptimistic(null); }, [checked]);

  const shown = optimistic ?? checked;
  const resolvedSize = size === "sm" ? "sm" : "md";

  const flip = async (e) => {
    e.stopPropagation();
    if (disabled || pending || !onChange) return;
    const next = !shown;
    setError(null);
    const result = onChange(next);
    if (result && typeof result.then === "function") {
      setOptimistic(next);
      setPending(true);
      try {
        await result;
      } catch (err) {
        if (alive.current) { setOptimistic(null); setError(err?.message || "Couldn't save — try again"); }
      } finally {
        if (alive.current) setPending(false);
      }
    }
  };

  const classes = [
    "ft-toggle",
    `ft-toggle--${resolvedSize}`,
    `ft-toggle--${tone}`,
    shown ? "is-on" : "",
    pending ? "is-pending" : "",
    error ? "is-error" : "",
    labelPosition === "start" ? "ft-toggle--label-start" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const status = shown ? statusOn : statusOff;

  return (
    <span className={classes}>
      <button
        ref={ref}
        type="button"
        id={id}
        role="switch"
        aria-checked={shown}
        aria-busy={pending || undefined}
        aria-labelledby={label != null && label !== "" ? `${baseId}-label` : undefined}
        aria-describedby={error ? `${baseId}-error` : undefined}
        disabled={disabled}
        className="ft-toggle__switch"
        onClick={flip}
        {...rest}
      >
        <span className="ft-toggle__knob" aria-hidden="true" />
      </button>
      {status ? <span className="ft-toggle__status" aria-hidden="true">{status}</span> : null}
      {label != null && label !== "" ? (
        <span className="ft-toggle__label" id={`${baseId}-label`} onClick={flip}>{label}</span>
      ) : null}
      {error ? <span className="ft-toggle__error" id={`${baseId}-error`} role="alert">{error}</span> : null}
    </span>
  );
});

export default Toggle;
